import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { computePillarBreakdown, type PillarBreakdown } from "@/lib/workScoreBreakdown";

export interface BlogPage {
  id: string;
  slug: string;
  city: string | null;
  neighborhood: string | null;
  title: string;
  intro_copy: string | null;
  meta_title: string | null;
  meta_description: string | null;
  is_published: boolean;
}

export interface BlogCafeEntry {
  id: string;
  display_order: number;
  description: string | null;
  cafe: {
    id: string;
    slug: string | null;
    name: string;
    address: string;
    city: string;
    neighborhood: string | null;
    cover_image_url: string | null;
    work_readiness_score: number | null;
    /** Signed in only. Empty for guests, who have no read access. */
    work_note_categories?: string[];
  };
  breakdown: PillarBreakdown;
}



export function useBlogPage(slug: string | undefined) {
  return useQuery({
    queryKey: ["blog-page", slug],
    enabled: !!slug,
    queryFn: async (): Promise<{ page: BlogPage; entries: BlogCafeEntry[] } | null> => {
      const { data: page, error: pageErr } = await supabase
        .from("blog_pages")
        .select("*")
        .eq("slug", slug!)
        .eq("is_published", true)
        .maybeSingle();

      if (pageErr) throw pageErr;
      if (!page) return null;

      const { data: entries, error: entriesErr } = await supabase
        .from("blog_cafe_entries")
        .select(
          `id, display_order, description,
           cafe:cafes!inner (
             id, slug, name, address, city, neighborhood,
             cover_image_url, work_readiness_score,
             cafe_amenities ( wifi_type, outlet_availability, seating_capacity, parking_available )
           )`
        )
        .eq("page_id", page.id)
        .order("display_order", { ascending: true });

      if (entriesErr) throw entriesErr;

      // Good to know categories are signed in only (anon has no grant on the
      // view), so fetch them in one extra query and skip it entirely for guests.
      const cafeIds = (entries ?? []).map((e: any) => e.cafe?.id).filter(Boolean);
      const noteCategories = new Map<string, string[]>();
      const { data: sessionData } = await supabase.auth.getSession();
      if (sessionData?.session && cafeIds.length > 0) {
        const { data: noteRows } = await supabase
          .from("cafe_browse_work_note_categories")
          .select("cafe_id, categories")
          .in("cafe_id", cafeIds);
        for (const r of noteRows ?? []) {
          if (r.cafe_id) noteCategories.set(r.cafe_id, (r.categories ?? []) as string[]);
        }
      }

      const mapped: BlogCafeEntry[] = (entries ?? [])
        .filter((e: any) => e.cafe)
        .map((e: any) => {
          const amen = Array.isArray(e.cafe.cafe_amenities)
            ? e.cafe.cafe_amenities[0]
            : e.cafe.cafe_amenities;
          const breakdown = computePillarBreakdown(amen ?? {});
          const score = e.cafe.work_readiness_score ?? 0;
          return {
            id: e.id,
            display_order: e.display_order,
            description: e.description,
            cafe: {
              id: e.cafe.id,
              slug: e.cafe.slug,
              name: e.cafe.name,
              address: e.cafe.address,
              city: e.cafe.city,
              neighborhood: e.cafe.neighborhood,
              cover_image_url: e.cafe.cover_image_url,
              work_readiness_score: score,
              work_note_categories: noteCategories.get(e.cafe.id),
            },
            breakdown,

          };
        });


      return { page: page as BlogPage, entries: mapped };
    },
  });
}

export function useOtherPublishedPages(currentSlug: string | undefined) {
  return useQuery({
    queryKey: ["blog-page-list", currentSlug],
    queryFn: async (): Promise<Pick<BlogPage, "slug" | "title" | "neighborhood" | "city">[]> => {
      const { data, error } = await supabase
        .from("blog_pages")
        .select("slug, title, neighborhood, city")
        .eq("is_published", true)
        .order("created_at", { ascending: false })
        .limit(12);
      if (error) throw error;
      return (data ?? []).filter((p) => p.slug !== currentSlug);
    },
  });
}
